import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminSecurityEditor() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/security-config", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setConfig(res.data);
            } catch (error) {
                toast.error("Error loading security config");
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const handleChange = (field, value) => {
        setConfig({ ...config, [field]: value });
    };

    const saveConfig = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/security-config", config, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Settings saved!");
        } catch {
            toast.error("Something went wrong");
        } finally {
            setSaving(false);
        }
    };

    const resetKey = async () => {
        const ok = await confirmSwal({
            title: `Reset the security key?`,
            text: "This will regenerate the security key.",
            icon: "question",
            confirmText: "Reset",
            confirmColor: "#d33",
        });
        if (!ok) return;

        try {
            const token = await auth.currentUser.getIdToken();
            const res = await axios.post("/api/security-config/reset-key", {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setConfig({ ...config, security_key: res.data.security_key });
            toast.info("Security key reset");
        } catch {
            toast.error("Something went wrong");
        }
    };

    if (loading) return <p>Loading security settings...</p>;
    if (!config) return <p>Error: Config missing</p>;

    return (
        <div className="">
            <h4>🔐 Registration Settings</h4>
            
            <div>
                <div className="mb-3 form-check">
                    <input
                        type="checkbox"
                        id="open_registration"
                        className="form-check-input"
                        checked={config.open_registration || false}
                        onChange={(e) => handleChange("open_registration", e.target.checked)}
                    />
                    <label htmlFor="open_registration" className="form-check-label">
                        Allow public registration
                    </label>
                    <div className="form-text">
                        When enabled, visitors can register for accounts on the login page.
                    </div>
                </div>

                <div className="mb-3 form-check">
                    <input
                        type="checkbox"
                        id="automatic_approval"
                        className="form-check-input"
                        checked={config.automatic_approval || false}
                        onChange={(e) => handleChange("automatic_approval", e.target.checked)}
                        disabled={!config.open_registration}
                    />
                    <label htmlFor="automatic_approval" className="form-check-label">
                        Automatically approve new registrations
                    </label>
                    <div className="form-text">
                        When enabled, new registrations are approved immediately. Otherwise, they require admin approval.
                    </div>
                </div>

                <button className="btn btn-admin" onClick={saveConfig} disabled={saving}>
                    {saving ? "Saving..." : "Save Settings"}
                </button>

                <div className="mt-4">
                    <h5>Advanced</h5>
                    <button onClick={resetKey} className="btn btn-outline-danger">
                        Reset Security Key
                    </button>
                    <div className="form-text">
                        Forces all users to re-authenticate on next login.
                    </div>
                </div>
            </div>
        </div>
    );
}
